import { For, Show, createSignal, onMount } from "solid-js";
import { api, type OrganizationRegistrationRequest } from "../shared";

function statusClasses(status: OrganizationRegistrationRequest["status"]) {
  if (status === "approved") return "bg-emerald-500/10 text-emerald-300 border-emerald-500/20";
  if (status === "rejected") return "bg-rose-500/10 text-rose-300 border-rose-500/20";
  return "bg-amber-500/10 text-amber-300 border-amber-500/20";
}

export function OrganizationRegistrationRequestsPanel() {
  const [requests, setRequests] = createSignal<OrganizationRegistrationRequest[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [busyId, setBusyId] = createSignal<string | null>(null);

  const pendingRequests = () => requests().filter((request) => request.status === "pending");
  const reviewedRequests = () => requests().filter((request) => request.status !== "pending");

  async function loadRequests() {
    setLoading(true);
    try {
      setRequests(await api.organizations.registrationRequests());
      setError("");
    } catch (err: any) {
      setError(err.message ?? "Failed to load organization registration requests");
    }
    setLoading(false);
  }

  async function approveRequest(id: string) {
    setBusyId(id);
    try {
      await api.organizations.approveRegistrationRequest(id);
      await loadRequests();
    } catch (err: any) {
      setError(err.message ?? "Approval failed");
    }
    setBusyId(null);
  }

  async function rejectRequest(id: string) {
    const reviewNote = window.prompt("Optional rejection reason:");
    if (reviewNote === null) return;
    setBusyId(id);
    try {
      await api.organizations.rejectRegistrationRequest(id, reviewNote);
      await loadRequests();
    } catch (err: any) {
      setError(err.message ?? "Rejection failed");
    }
    setBusyId(null);
  }

  onMount(loadRequests);

  return (
    <section class="rounded-xl border border-gray-800 bg-gray-900/70 p-5">
      <div class="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 class="text-base font-semibold text-white">Organization Registration Requests</h2>
          <p class="text-sm text-gray-400">Approve or reject pending organization onboarding requests.</p>
        </div>
        <button onClick={loadRequests} class="px-3 py-2 rounded-lg border border-gray-700 text-sm text-gray-200 hover:bg-gray-800 cursor-pointer">
          Refresh
        </button>
      </div>

      <Show when={error()}>
        <div class="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">{error()}</div>
      </Show>

      <Show when={!loading()} fallback={<div class="text-sm text-gray-400">Loading organization registration requests...</div>}>
        <div class="space-y-3">
          <Show when={pendingRequests().length > 0} fallback={<div class="rounded-lg border border-gray-800 bg-gray-950/60 px-4 py-4 text-sm text-gray-400">No pending organization registration requests.</div>}>
            <For each={pendingRequests()}>
              {(request) => (
                <div class="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
                  <div class="flex items-start justify-between gap-4">
                    <div class="space-y-2">
                      <div class="flex items-center gap-2">
                        <span class={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${statusClasses(request.status)}`}>{request.status}</span>
                        <span class="text-xs text-gray-500">{new Date(request.createdAt).toLocaleString()}</span>
                      </div>
                      <div class="text-sm text-gray-100 font-medium">{request.organizationName}</div>
                      <div class="text-sm text-gray-400">Slug: <span class="font-mono text-gray-200">{request.requestedSlug}</span></div>
                      <div class="text-sm text-gray-400">Requester: <span class="text-gray-200">{request.requesterDisplayName}</span> <span class="text-gray-500">({request.requesterEmail})</span></div>
                    </div>
                    <div class="flex gap-2">
                      <button
                        onClick={() => approveRequest(request.id)}
                        disabled={busyId() === request.id}
                        class="px-3 py-2 rounded-lg bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50 cursor-pointer"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => rejectRequest(request.id)}
                        disabled={busyId() === request.id}
                        class="px-3 py-2 rounded-lg border border-rose-500/40 text-sm font-medium text-rose-200 hover:bg-rose-500/10 disabled:opacity-50 cursor-pointer"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </Show>
        </div>

        <Show when={reviewedRequests().length > 0}>
          <div class="mt-6">
            <h3 class="text-sm font-semibold text-gray-300 mb-3">Reviewed</h3>
            <div class="space-y-3">
              <For each={reviewedRequests()}>
                {(request) => (
                  <div class="rounded-lg border border-gray-800 bg-gray-950/50 p-4">
                    <div class="space-y-2">
                      <div class="flex items-center gap-2">
                        <span class={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${statusClasses(request.status)}`}>{request.status}</span>
                        <span class="text-xs text-gray-500">{request.reviewedAt ? new Date(request.reviewedAt).toLocaleString() : new Date(request.createdAt).toLocaleString()}</span>
                      </div>
                      <div class="text-sm text-gray-100 font-medium">{request.organizationName}</div>
                      <div class="text-sm text-gray-400">Slug: <span class="font-mono text-gray-200">{request.requestedSlug}</span></div>
                      <div class="text-sm text-gray-400">Requester: <span class="text-gray-200">{request.requesterDisplayName}</span> <span class="text-gray-500">({request.requesterEmail})</span></div>
                      <Show when={request.createdOrganizationSlug}>
                        <div class="text-sm text-gray-400">Created org: <span class="font-mono text-gray-200">{request.createdOrganizationSlug}</span></div>
                      </Show>
                      <Show when={request.reviewNote}>
                        <div class="text-sm text-gray-400">Review note: <span class="text-gray-200">{request.reviewNote}</span></div>
                      </Show>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </div>
        </Show>
      </Show>
    </section>
  );
}
